import React, { useCallback, useEffect, useState } from 'react';
import { ShieldOff, Loader2, RefreshCcw, Plus, Trash2, Cookie } from 'lucide-react';
import api from '@/lib/axios';

export default function MarketplaceSuppressionPanel() {
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [novo, setNovo] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [s, o] = await Promise.all([
        api.get(`/admin/marketplace/suppression${q ? `?q=${encodeURIComponent(q)}` : ''}`),
        api.get('/admin/marketplace/consents-overview'),
      ]);
      setItems(s.data.items || []); setOverview(o.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar.'); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!novo.trim()) return;
    try { await api.post('/admin/marketplace/suppression', { email: novo.trim(), motivo: 'manual' }); setNovo(''); load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Falha a adicionar.'); }
  };
  const remove = async (email) => { await api.delete(`/admin/marketplace/suppression/${encodeURIComponent(email)}`); load(); };

  const Card = ({ label, value, sub }) => (
    <div className="rounded-xl border border-slate-200 p-3 text-center">
      <p className="text-lg font-bold text-slate-900">{value}</p>
      <p className="text-[10px] font-semibold uppercase text-slate-500">{label}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="suppression-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <ShieldOff className="h-5 w-5 text-emerald-600" /> Consentimentos e lista de supressão
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>
      {err && <p className="mt-2 text-xs font-semibold text-red-600">{err}</p>}

      {overview && (
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-5" data-testid="consents-overview">
          <Card label="Decisões cookies" value={overview.consentimentos.total} />
          <Card label="% Analytics" value={`${overview.consentimentos.taxa_analytics}%`} />
          <Card label="% Publicidade" value={`${overview.consentimentos.taxa_ads}%`} />
          <Card label="Aceitar tudo / Recusar" value={`${overview.consentimentos.accept_all}/${overview.consentimentos.reject_all}`} />
          <Card label="Em supressão" value={overview.supressao.total} sub="cancelou/queixa/bounce" />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input value={novo} onChange={(e) => setNovo(e.target.value)} placeholder="email@exemplo.com"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs" data-testid="suppression-new-email" />
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid="suppression-add">
          <Plus className="h-3.5 w-3.5" /> Adicionar
        </button>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar…"
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs" data-testid="suppression-search" />
      </div>

      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
      ) : (
        <ul className="mt-3 space-y-1.5" data-testid="suppression-list">
          {items.length === 0 && <li className="text-xs text-slate-500">Lista de supressão vazia.</li>}
          {items.map((it) => (
            <li key={it.email} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs">
              <span><span className="font-semibold text-slate-800">{it.email}</span> <span className="text-[10px] text-slate-500">· {it.motivo}</span></span>
              <button type="button" onClick={() => remove(it.email)} className="text-red-500 hover:text-red-700" data-testid={`suppression-remove-${it.email}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 inline-flex items-center gap-1 text-[10px] text-slate-400"><Cookie className="h-3 w-3" /> Boosts e alertas nunca contactam emails em supressão.</p>
    </section>
  );
}
